import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../lib/api';
import { motion } from 'framer-motion';
import { Lock, Mail, ArrowRight } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_emergent-bd/artifacts/anqnj33g_Breach.png';
const INDEPTH_LOGO = '/indepth-intelligence.webp';

const ease = [0.16, 1, 0.3, 1];

const reveal = (delay) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: delay / 1000, duration: 0.5, ease },
});

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/17272605/pexels-photo-17272605.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/90 via-[#0A1628]/80 to-[#0A1628]" />

      <div className="relative z-10 w-full max-w-md">
        {/* Staggered reveal */}
        <div className="text-center mb-6">
          {/* 1. Whale logo rises */}
          <motion.div {...reveal(200)} className="mb-2">
            <img
              src={LOGO_URL}
              alt="BREACH"
              className="h-48 w-48 mx-auto object-contain"
              data-testid="login-whale-logo"
            />
          </motion.div>

          {/* 2. "BREACH" wordmark */}
          <motion.h1
            {...reveal(600)}
            className="text-4xl sm:text-5xl font-bold tracking-[0.06em] text-[#00D4C8] leading-none"
            data-testid="login-brand-name"
          >
            BREACH
          </motion.h1>

          {/* 3. Gold divider between BREACH and IN DEPTH Intelligence */}
          <motion.div
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{ delay: 0.75, duration: 0.4, ease }}
            className="h-[2px] bg-[#C8A97E] w-56 mx-auto mt-4 mb-3 origin-center"
            data-testid="login-gold-divider"
          />

          {/* 4. IN DEPTH Intelligence logo */}
          <motion.div {...reveal(900)}>
            <img
              src={INDEPTH_LOGO}
              alt="IN DEPTH Intelligence"
              className="h-20 w-auto mx-auto object-contain"
              data-testid="login-indepth-logo"
            />
          </motion.div>
        </div>

        {/* 6. Sign in form fades in last */}
        <motion.div {...reveal(1700)}>
          <div className="card-surface p-8">
            <h2 className="font-heading text-xl font-bold italic text-[#C8A97E] text-center whitespace-nowrap mb-6">Breakthrough to the Right People</h2>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm"
                data-testid="login-error"
              >
                {error}
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-medium uppercase tracking-[0.2em] text-[#00D4C8] mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A3B5C7]" strokeWidth={1.5} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field has-icon"
                    placeholder="you@example.com"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium uppercase tracking-[0.2em] text-[#00D4C8] mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A3B5C7]" strokeWidth={1.5} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field has-icon"
                    placeholder="Enter your password"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2 mt-6"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="inline-block w-5 h-5 border-2 border-[#0A1628]/30 border-t-[#0A1628] rounded-full animate-spin" />
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-4 h-4" strokeWidth={2} />
                  </>
                )}
              </button>
            </form>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.p
          {...reveal(2000)}
          className="text-center text-[#A3B5C7] text-sm mt-8 opacity-60"
        >
          BD Intelligence
        </motion.p>
      </div>
    </div>
  );
};

export default LoginPage;
